import type { PageContent } from "../types";

export const p_search_in_a_pandemic_changing_user_behaviour: PageContent = {
  slug: "search-in-a-pandemic-changing-user-behaviour",
  title: `Search in a Pandemic: Changing User Behaviour | Phil Carr SEO Consultant`,
  description: `The COVID‑19 pandemic transformed search behaviour in unprecedented ways. Lockdowns and remote work increased dependence on digital services for shopping, education, entertainment and social interaction.…`,
  date: "",
  lastUpdated: "2026-05-09",
  body: `# Search in a Pandemic: Changing User Behaviour

![Search in a Pandemic: Changing User Behaviour featured image](/images/blog/generated/search-in-a-pandemic-changing-user-behaviour.jpg)

**Published:** June 15, 2020

## The Impact of Global Events

The COVID‑19 pandemic transformed search behaviour in unprecedented ways. Lockdowns and remote work increased dependence on digital services for shopping, education, entertainment and social interaction.

Queries related to health information, food delivery, home improvement and outdoor activities surged. Local searches for “open now” and “curbside pickup” spiked as people sought safe options.

Businesses that pivoted quickly to meet these needs saw significant gains, while others struggled.

## Adapting Your Content and Offerings

Listen to your audience and adjust your offerings accordingly. If you run a restaurant, emphasise delivery and takeout options.

If you sell retail goods, highlight online ordering and contactless pickup. Provide clear, up‑to‑date information about safety measures and availability.

Create content that addresses common pandemic‑related questions in your niche. For example, event planners might discuss virtual event options, while fitness trainers could share tips for working out at home.

## Long‑Term Effects on Search

Some changes that emerged during the pandemic are likely to stick. People became accustomed to shopping online and appreciating convenience.

Hybrid work arrangements may keep remote search queries high. Build resilience by maintaining the digital services and content you developed during the pandemic, and be prepared to pivot quickly should user behaviour shift again.

## What businesses can still learn from this shift

The useful lesson is not only about lockdown behaviour. It is that search demand can change quickly when customers feel uncertainty, need reassurance, or have to compare options remotely. Pages that explain availability, process, delivery, booking, and response times usually perform better during those periods because they reduce friction at the point of enquiry.

That same principle still applies to local SEO. Businesses should keep service information current, review their most visited pages after major market changes, and update FAQs when customer questions start to change.

## SEO Takeaways and Next Steps

2020 taught us that adaptability is essential. If you need help analysing changing user behaviour or tailoring your content to meet evolving needs, contact info@phil‑carr.co.uk or 01226 697 325.

If you need help with your SEO strategy, get in touch by emailing [info@phil-carr.co.uk](mailto:info@phil-carr.co.uk) or calling 01226 697 325.

## Key Takeaways

- **The Impact of Global Events:** The COVID‑19 pandemic transformed search behaviour in unprecedented ways.
- **Adapting Your Content and Offerings:** Listen to your audience and adjust your offerings accordingly.
- **Long‑Term Effects on Search:** Some changes that emerged during the pandemic are likely to stick.

## Update — May 2026

Reviewed in May 2026. Nothing structural has changed in how I'd approach this — the shift is that clients are asking for shorter, sharper reporting cycles, and the framework below adapts to that without needing a rewrite.

## About the author

Phil Carr is an SEO consultant and web strategist focused on helping businesses
grow through technical optimisation, content strategy, and data-driven decision making. His
work combines hands-on experience with ongoing research into algorithm updates and emerging
search trends.

[Read Phil’s bio](/#authorModal)

## Other News Articles

[![2024 Algorithm Updates – Navigating a Year of Core Changes and Spam Crackdowns featured image](/images/blog/generated/2024-algorithm-updates-navigating-a-year-of-core-changes-and-spam-crackdowns.jpg)](/blog/2024-algorithm-updates-navigating-a-year-of-core-changes-and-spam-crackdowns)

### [2024 Algorithm Updates – Navigating a Year of Core Changes and Spam Crackdowns](/blog/2024-algorithm-updates-navigating-a-year-of-core-changes-and-spam-crackdowns)

[![2023 SEO Lessons and Forward View featured image](/images/blog/generated/2023-seo-lessons-and-forward-view.jpg)](/blog/2023-seo-lessons-and-forward-view)

### [2023 SEO Lessons and Forward View](/blog/2023-seo-lessons-and-forward-view)

[![A Practical Plan for AI-Driven Search – A Ninety Day Playbook featured image](/images/blog/generated/a-practical-plan-for-ai-driven-search-a-ninety-day-playbook.jpg)](/blog/a-practical-plan-for-ai-driven-search-a-ninety-day-playbook)

### [A Practical Plan for AI-Driven Search – A Ninety Day Playbook](/blog/a-practical-plan-for-ai-driven-search-a-ninety-day-playbook)

[![Looking Ahead: Lessons from 2024 for the Future featured image](/images/blog/generated/looking-ahead-lessons-from-2024-for-the-future.jpg)](/blog/looking-ahead-lessons-from-2024-for-the-future)

### [Looking Ahead: Lessons from 2024 for the Future](/blog/looking-ahead-lessons-from-2024-for-the-future)

We use cookies to improve your experience, analyse site traffic, and understand how you interact with
our content. Read our [cookie policy](/cookies) to learn more.

Reject optionalAccept cookies [Privacy policy](/privacy-policy)

reCAPTCHA

Recaptcha requires verification.

protected by **reCAPTCHA**`,
};
